/**
 * Cloud Drive Connect — OAuth connection flow for Google Drive / OneDrive.
 * Shows connection status, connect/disconnect buttons, and sync controls.
 */
import { useState, useEffect } from "react";
import { Cloud, CloudOff, RefreshCw, CheckCircle, AlertTriangle, ExternalLink, Info } from "lucide-react";
import { api } from "../../lib/api.ts";
import { CloudFolderPicker } from "./CloudFolderPicker.tsx";

interface CloudDriveConnectProps {
  projectId: string;
  onSyncComplete?: () => void;
}

interface CloudStatus {
  connected: boolean;
  folderSelected: boolean;
  provider: string | null;
  folderName: string | null;
  lastSynced: string | null;
  filesProcessed: number;
  fieldsUpdated: number;
  pendingReviews: number;
  availableProviders: string[];
}

export function CloudDriveConnect({ projectId, onSyncComplete }: CloudDriveConnectProps) {
  const [status, setStatus] = useState<CloudStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("cloud_error") || null;
  });

  const load = () => {
    setLoading(true);
    api.getCloudStatus(projectId)
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [projectId]);

  const handleConnect = (provider: string) => {
    // Redirect to OAuth flow — server returns the auth URL
    api.startCloudConnect(projectId, provider)
      .then(({ authUrl }) => { window.location.href = authUrl; })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to start connection"));
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.disconnectCloud(projectId);
      load();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.triggerCloudSync(projectId);
      // Sync runs in background — poll for completion
      const poll = setInterval(() => {
        api.getCloudStatus(projectId).then((s) => {
          setStatus(s);
          // Stop polling once lastSynced changes (sync finished)
          if (s.lastSynced && s.lastSynced !== status?.lastSynced) {
            clearInterval(poll);
            setSyncing(false);
            onSyncComplete?.();
          }
        }).catch(() => {});
      }, 3000);
      // Stop polling after 5 minutes max
      setTimeout(() => { clearInterval(poll); setSyncing(false); }, 300000);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Sync failed");
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="h-16 bg-surface-tertiary rounded-xl animate-pulse" />;
  }

  // No cloud providers configured on the server — show a discoverable placeholder
  // so users know the feature exists even before it's enabled in their environment.
  if (!status || status.availableProviders.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-3">
        <Cloud size={18} className="text-text-tertiary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium text-text-primary flex items-center gap-1.5">
            Cloud drive sync
            <CloudPolicyPopover />
          </div>
          <div className="text-[12px] text-text-tertiary">
            Sync documents from Google Drive or OneDrive automatically. Coming soon — currently being enabled.
          </div>
        </div>
      </div>
    );
  }

  // Connected but no folder selected — show folder picker
  if (status.connected && !status.folderSelected) {
    return <CloudFolderPicker projectId={projectId} onSelect={load} />;
  }

  // Connected + folder selected — show status
  if (status.connected && status.provider) {
    const providerName = status.provider === "google_drive" ? "Google Drive" : "OneDrive";
    const lastSync = status.lastSynced
      ? new Date(status.lastSynced).toLocaleString()
      : "Never";

    return (
      <div className="bg-surface border border-border rounded-2xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Cloud size={16} className="text-brand" />
            <span className="text-[15px] font-medium">Connected to {providerName}</span>
            <CloudPolicyPopover />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1 px-2.5 py-1 bg-brand-light text-brand rounded-lg text-[13px] font-medium hover:bg-brand/10 disabled:opacity-50"
            >
              <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync now"}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="flex items-center gap-1 px-2.5 py-1 text-text-tertiary hover:text-danger text-[13px] disabled:opacity-50"
            >
              <CloudOff size={12} /> Disconnect
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 text-center text-[13px]">
          <div className="bg-surface-secondary rounded-lg px-2 py-1.5">
            <div className="text-text-tertiary">Folder</div>
            <div className="font-medium truncate" title={status.folderName || ""}>{status.folderName || "—"}</div>
          </div>
          <div className="bg-surface-secondary rounded-lg px-2 py-1.5">
            <div className="text-text-tertiary">Last sync</div>
            <div className="font-medium">{lastSync}</div>
          </div>
          <div className="bg-surface-secondary rounded-lg px-2 py-1.5">
            <div className="text-text-tertiary">Fields updated</div>
            <div className="font-medium">{status.fieldsUpdated}</div>
          </div>
        </div>

        {status.pendingReviews > 0 && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-warning-light rounded-lg text-[13px] text-warning">
            <AlertTriangle size={14} />
            {status.pendingReviews} file{status.pendingReviews > 1 ? "s" : ""} need{status.pendingReviews === 1 ? "s" : ""} your review
          </div>
        )}

        {error && (
          <div className="mt-3 px-3 py-2 bg-danger-light rounded-lg text-[13px] text-danger">{error}</div>
        )}
      </div>
    );
  }

  // Disconnected state — show connect options
  return (
    <div className="border border-dashed border-border rounded-2xl p-5 mb-4 text-center">
      <Cloud size={24} className="text-text-tertiary mx-auto mb-2" />
      <p className="text-[15px] font-medium mb-1 flex items-center justify-center gap-1.5">
        Connect your project folder
        <CloudPolicyPopover />
      </p>
      <p className="text-[13px] text-text-tertiary mb-4">
        enereg will watch for new documents and automatically extract project data. We only read files, never modify them.
      </p>

      <div className="flex items-center justify-center gap-3">
        {status.availableProviders.includes("google_drive") && (
          <button
            onClick={() => handleConnect("google_drive")}
            className="flex items-center gap-2 px-4 py-2 border border-border bg-surface-secondary text-text-primary rounded-xl text-[14px] font-medium hover:bg-surface-tertiary hover:border-border-secondary transition-colors"
          >
            <Cloud size={16} /> Google Drive
          </button>
        )}
        {status.availableProviders.includes("onedrive") && (
          <button
            onClick={() => handleConnect("onedrive")}
            className="flex items-center gap-2 px-4 py-2 border border-border bg-surface-secondary text-text-primary rounded-xl text-[14px] font-medium hover:bg-surface-tertiary hover:border-border-secondary transition-colors"
          >
            <Cloud size={16} /> OneDrive
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 bg-danger-light rounded-lg text-[13px] text-danger">{error}</div>
      )}
    </div>
  );
}

// ─── Cloud Policy Popover ───
// Hover/click info icon that explains how synced files are used.
// Pure CSS hover (group-hover) plus click toggle for touch users.

function CloudPolicyPopover() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex group">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        aria-label="How your files are used"
        className="text-text-tertiary hover:text-text-secondary transition-colors"
      >
        <Info size={13} />
      </button>
      <div
        className={`absolute z-50 right-0 top-5 w-72 bg-surface border border-border rounded-xl shadow-lg p-3 text-left transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
        }`}
      >
        <div className="text-[13px] font-medium text-text-primary mb-1.5 flex items-center gap-1.5">
          <CheckCircle size={12} className="text-brand" /> How your cloud files are used
        </div>
        <ul className="text-[12px] text-text-secondary leading-relaxed space-y-1">
          <li><span className="text-brand">✓</span> Read-only access — we never modify, move, or delete your cloud files.</li>
          <li><span className="text-brand">✓</span> Only the folder you select is scanned. Nothing else in your drive.</li>
          <li><span className="text-brand">✓</span> Cloud files are read in memory, processed, then discarded. No copies stored.</li>
          <li><span className="text-brand">✓</span> Extracted field values are saved (not the files themselves).</li>
          <li><span className="text-brand">✓</span> Manually uploaded files are stored securely for preview and re-extraction.</li>
          <li><span className="text-brand">✓</span> Disconnect at any time. Tokens and folder selection are deleted.</li>
        </ul>
        <div className="text-[11px] text-text-tertiary mt-2 pt-2 border-t border-border">
          OAuth scope: <code className="font-mono">drive.readonly</code>
        </div>
      </div>
    </span>
  );
}
